export function normalizeRecordedDateValue(value) {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
}

export function loadMediaMetadata(file) {
  return new Promise((resolve) => {
    if (!file || typeof document === 'undefined') {
      resolve({ durationSeconds: null, recordedDateUtc: null });
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const mediaElement = document.createElement(file.type?.startsWith('audio/') ? 'audio' : 'video');
    let settled = false;

    const finalize = (metadata) => {
      if (settled) {
        return;
      }

      settled = true;
      try {
        mediaElement.removeAttribute('src');
        mediaElement.load();
      } catch {
        // Ignore cleanup issues.
      }
      URL.revokeObjectURL(objectUrl);
      resolve(metadata);
    };

    mediaElement.preload = 'metadata';
    mediaElement.onloadedmetadata = () => {
      const durationSeconds = Number.isFinite(mediaElement.duration) && mediaElement.duration > 0
        ? mediaElement.duration
        : null;
      const recordedDateUtc = normalizeRecordedDateValue(
        file.lastModified ? new Date(file.lastModified) : null
      );
      finalize({ durationSeconds, recordedDateUtc });
    };
    mediaElement.onerror = () => {
      finalize({
        durationSeconds: null,
        recordedDateUtc: normalizeRecordedDateValue(file.lastModified ? new Date(file.lastModified) : null)
      });
    };
    mediaElement.src = objectUrl;
  });
}

export function createUploadSelectionHelpers({
  maxMediaFiles,
  createLocalId,
  createMediaItem,
  getExtension,
  getMediaCountLimitMessage,
  getMediaFileValidationMessage,
  buildCaseFileItems,
  shouldCountLocalUploadItem,
  normalizeDisplayName,
  getEffectiveTranscriptRecord,
  getRecordMediaId,
  getRecordSizeInBytes
}) {
  function createSelectionFeedbackItem(file, reason, tone = 'error') {
    return {
      id: `${createLocalId()}-${file?.name ?? 'file'}`,
      fileName: file?.name ?? '',
      extension: getExtension(file?.name),
      size: file?.size ?? 0,
      reason,
      tone
    };
  }

  function createSelectionSummaryDialog(acceptedItems, rejectedItems) {
    return {
      tone: 'error',
      title: 'File selection summary',
      message: '',
      selectionSummary: {
        accepted: acceptedItems,
        rejected: rejectedItems
      }
    };
  }

  function getUploadLimitTotals(localItems = [], remoteRecords = []) {
    const localItemsArray = Array.isArray(localItems) ? localItems : [];
    const localPendingItems = localItemsArray.filter((item) => shouldCountLocalUploadItem(item, remoteRecords));
    const remoteItems = buildCaseFileItems(Array.isArray(remoteRecords) ? remoteRecords : []);

    const localKnownMediaIds = new Set(
      localPendingItems
        .map((item) => normalizeDisplayName(item?.mediaId))
        .filter((value) => Boolean(value))
    );
    const remoteTotals = remoteItems.reduce((accumulator, item) => {
      const mediaRecord = item?.mediaRecord ?? null;
      const transcriptRecord = getEffectiveTranscriptRecord(mediaRecord, item?.transcriptRecord ?? null);
      const remoteMediaId = normalizeDisplayName(getRecordMediaId(mediaRecord));

      // Avoid double-counting only when the local pending item already has the
      // same persisted mediaId. Completed local rows are not counted here because
      // the remote case-file list is the source of truth after upload.
      if (remoteMediaId && localKnownMediaIds.has(remoteMediaId)) {
        return accumulator;
      }

      accumulator.mediaCount += 1;
      accumulator.totalBytes += getRecordSizeInBytes(mediaRecord);
      accumulator.totalBytes += getRecordSizeInBytes(transcriptRecord);
      return accumulator;
    }, { mediaCount: 0, totalBytes: 0 });

    const localTotals = localPendingItems.reduce((accumulator, item) => {
      accumulator.mediaCount += 1;
      accumulator.totalBytes += item?.mediaFile?.size ?? 0;
      accumulator.totalBytes += item?.transcriptFile?.size ?? 0;
      return accumulator;
    }, { mediaCount: 0, totalBytes: 0 });

    return {
      remoteMediaCount: remoteTotals.mediaCount,
      remoteBytes: remoteTotals.totalBytes,
      localMediaCount: localTotals.mediaCount,
      localBytes: localTotals.totalBytes,
      totalMediaCount: remoteTotals.mediaCount + localTotals.mediaCount,
      totalBytes: remoteTotals.totalBytes + localTotals.totalBytes
    };
  }

  function getUploadLimitValidationMessage(localItems = [], remoteRecords = []) {
    const totals = getUploadLimitTotals(localItems, remoteRecords);

    if (totals.totalMediaCount > maxMediaFiles) {
      return getMediaCountLimitMessage();
    }

    return '';
  }

  function classifyMediaFilesForSelection(files, currentItems = [], remoteRecords = []) {
    const incomingFiles = Array.isArray(files) ? files : [];
    const acceptedFiles = [];
    const rejectedFiles = [];
    const currentItemsArray = Array.isArray(currentItems) ? currentItems : [];
    const remoteRecordsArray = Array.isArray(remoteRecords) ? remoteRecords : [];

    let runningItems = [...currentItemsArray];

    for (const file of incomingFiles) {
      const validationMessage = getMediaFileValidationMessage(file);
      if (validationMessage) {
        rejectedFiles.push(createSelectionFeedbackItem(file, validationMessage));
        continue;
      }

      const candidateItems = [...runningItems, createMediaItem(acceptedFiles.length, file)];
      const totals = getUploadLimitTotals(candidateItems, remoteRecordsArray);

      if (totals.totalMediaCount > maxMediaFiles) {
        rejectedFiles.push(createSelectionFeedbackItem(file, getMediaCountLimitMessage()));
        continue;
      }

      acceptedFiles.push(file);
      runningItems = candidateItems;
    }

    return {
      acceptedFiles,
      rejectedFiles
    };
  }

  return {
    createSelectionFeedbackItem,
    createSelectionSummaryDialog,
    classifyMediaFilesForSelection,
    getUploadLimitTotals,
    getUploadLimitValidationMessage
  };
}
